import React, { useState, useEffect } from "react";
import { PlusCircle, Pencil, Trash2 } from "lucide-react";
import axios from "axios";
import { BaseUrl } from "../App";
import { FaEye, FaEyeSlash } from "react-icons/fa";

// import { Circles } from "react-loader-spinner";

import { FadeLoader } from 'react-spinners';


const Staff = () => {
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAddSidebarOpen, setIsAddSidebarOpen] = useState(false);
  const [isEditSidebarOpen, setIsEditSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchStaffData = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/staff.php`,
        {
          action: "all_staff_details",
          api_key: "1",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "true") {
        setTableData(response.data.response);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      alert("Error: Something went wrong while fetching the staff.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/staff.php`,
        {
          action: "add_staff",
          api_key: "1",
          fullname: formData.name,
          email: formData.email,
          pass: formData.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "false") {
        alert(response.data.response); // This will show: Staff already exist
        setFormData({ id: null, name: "", email: "", password: "" });
        setIsAddSidebarOpen(false);
      }

      if (response.data.status === "true") {
        setSuccessMessage("Add Staff successfully!");
        alert("Staff added successfully!");
        fetchStaffData();
        setFormData({ id: null, name: "", email: "", password: "" });
        setIsAddSidebarOpen(false);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      alert("Error: Something went wrong while adding the staff.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    if (!formData.email) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/staff.php`,
        {
          action: "update_staff",
          api_key: "1",
          id: formData.id,
          email: formData.email,
          pass: formData.password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.status === "true") {
        setSuccessMessage("Staff Updated successfully!");
        alert("Staff updated successfully!");
        fetchStaffData();
        setFormData({ id: null, name: "", email: "", password: "" });
        setIsEditSidebarOpen(false);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      alert("Error: Something went wrong while updating the staff.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    const row = tableData.find((row) => row.id === id);
    setFormData({
      id: row.id,
      name: row.fullname,
      email: row.email,
      password: row.password,
    });
    setIsEditSidebarOpen(true);
  };

  const toggleAddSidebar = () => {
    setFormData({
      id: null,
      name: "",
      email: "",
      password: "",
    });
    setIsAddSidebarOpen(!isAddSidebarOpen);
  };

  const toggleEditSidebar = () => {
    setIsEditSidebarOpen(!isEditSidebarOpen);
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${BaseUrl}/backend/api/staff.php`,
        {
          action: "delete_staff",
          api_key: "1",
          id: deleteId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.data.status === "true") {
        setSuccessMessage("Delete Staff Successfully!");
        alert("Staff deleted successfully!");
        fetchStaffData();
      } else {
        alert("Error: Unable to delete staff.");
      }
    } catch (error) {
      alert("Error: Something went wrong while deleting the staff.");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteId(null);
  };

  const filteredData = tableData.filter(
    (row) =>
      (row.fullname || "")
        .toLowerCase()
        .includes((searchQuery || "").toLowerCase()) ||
      (row.email || "")
        .toLowerCase()
        .includes((searchQuery || "").toLowerCase())
  );

  useEffect(() => {
    fetchStaffData();
  }, []);

  const StaffCard = ({ row }) => (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="font-semibold">ID: {row.id}</span>
        <div className="flex gap-2">
          <button
            onClick={() => handleEdit(row.id)}
            className="text-green-700 hover:text-green-400"
          >
            <Pencil className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="text-red-500 hover:text-red-600"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="space-y-1">
        <p>
          <span className="font-medium">Name:</span> {row.fullname}
        </p>
        <p>
          <span className="font-medium">Email:</span> {row.email}
        </p>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         
        <div className="w-full bg-white relative">
               {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
          {/* Add Sidebar */}
          <div
            className={`fixed top-0 right-0 h-full w-full md:max-w-md bg-white shadow-lg z-50 transform ${
              isAddSidebarOpen ? "translate-x-0" : "translate-x-full"
            } transition-all duration-500 ease-in-out`}
          >
            <div className="p-4 flex justify-between items-center bg-blue-500 text-white">
              <h2 className="text-lg font-semibold">Add Staff</h2>
              <button onClick={toggleAddSidebar} className="text-2xl font-bold">
                &times;
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmitAdd} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Name*"
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address*"
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
                {/* <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              className="w-full p-3 border border-gray-300 rounded-lg"
              required
            /> */}
                <div className="relative w-full">
                  {/* Password Input Field */}
                  <input
                    type={showPassword ? "text" : "password"} // Toggle between text & password
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                    required
                  />

                  {/* Eye Icon Button */}
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FaEyeSlash size={20} />
                    ) : (
                      <FaEye size={20} />
                    )}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                >
                  {loading ? "Processing..." : "Submit"}
                </button>
              </form>
            </div>
          </div>

          {/* Edit Sidebar */}
          <div
            className={`fixed top-0 right-0 h-full w-full md:max-w-md bg-white shadow-lg z-50 transform ${
              isEditSidebarOpen ? "translate-x-0" : "translate-x-full"
            } transition-all duration-500 ease-in-out`}
          >
            <div className="p-4 flex justify-between items-center bg-blue-500 text-white">
              <h2 className="text-lg font-semibold">Edit Staff</h2>
              <button
                onClick={toggleEditSidebar}
                className="text-2xl font-bold"
              >
                &times;
              </button>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmitUpdate} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  disabled
                  className="w-full p-3 border border-gray-300 rounded-lg bg-gray-200"
                  required
                />
                <p className="text-red-500 text-sm">
                  You can only update email and password.
                </p>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address*"
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
                <div className="relative w-full">
                  {/* Password Input Field */}
                  <input
                    type={showPassword ? "text" : "password"} // Toggle between text & password
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                  />

                  {/* Eye Icon Button */}
                  <button
                    type="button"
                    className="absolute right-3 top-3 text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FaEyeSlash size={20} />
                    ) : (
                      <FaEye size={20} />
                    )}
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full p-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                >
                  {loading ? "Processing..." : "Update"}
                </button>
              </form>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-4 md:pl-8 lg:pl-60">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Staff..."
                className="p-3 w-full md:w-96 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              />
              <button
                onClick={toggleAddSidebar}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold text-lg py-3 px-8 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <PlusCircle className="w-6 h-6" />
                Add
              </button>
            </div>

            {/* Responsive Table */}
            <div className="w-full overflow-hidden">
              {isMobile ? (
                <div className="space-y-4">
                  {filteredData.length > 0 ? (
                    filteredData.map((row) => (
                      <StaffCard key={row.id} row={row} />
                    ))
                  ) : (
                    <div className="text-center py-4 bg-white rounded-lg shadow">
                      No staff found.
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg">
                    <thead className="bg-blue-100 text-left">
                      <tr>
                        <th className="px-4 py-2 border-b">ID</th>
                        <th className="px-4 py-2 border-b">Name</th>
                        <th className="px-4 py-2 border-b">Email</th>
                        <th className="px-4 py-2 border-b">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.length > 0 ? (
                        filteredData.map((row) => (
                          <tr key={row.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border-b">{row.id}</td>
                            <td className="px-4 py-2 border-b">
                              {row.fullname}
                            </td>
                            <td className="px-4 py-2 border-b">{row.email}</td>
                            <td className="px-4 py-2 border-b">
                              <button
                                onClick={() => handleEdit(row.id)}
                                className="text-green-700 hover:text-green-400 mr-2"
                              >
                                <Pencil className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(row.id)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            No staff found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Overlay */}
          {(isAddSidebarOpen || isEditSidebarOpen || isDeleteConfirmOpen) && (
            <div
              className="fixed inset-0 bg-gray-800 bg-opacity-50 z-40"
              onClick={() => {
                if (isAddSidebarOpen) setIsAddSidebarOpen(false);
                if (isEditSidebarOpen) setIsEditSidebarOpen(false);
                if (isDeleteConfirmOpen) cancelDelete();
              }}
            />
          )}

          {/* Delete Confirmation Modal */}
          {isDeleteConfirmOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
                <h3 className="text-xl font-semibold mb-4">Confirm Delete</h3>
                <p className="mb-4">
                  Are you sure you want to delete this entry?
                </p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={confirmDelete}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                  <button
                    onClick={cancelDelete}
                    className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      
    </div>
  );
};

export default Staff;
